import Money from "@dashboard/components/Money";
import Skeleton from "@dashboard/components/Skeleton";
import { orderUrl } from "@dashboard/orders/urls";
import { Box, List, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { OrderToday } from "../../utils";
import { HomeProductListItem } from "../HomeProductList/HomeProductListItem";
import { homeSalesPanelMessages } from "./messages";

const MAX_LINE_THUMBNAILS = 3;

// Number, time, items, customer, amount. Only the items column may shrink,
// and the customer column ellipsizes past a long email, so the number and
// the amount are always whole. Fixed widths keep the columns aligned across
// rows, since each row is its own grid.
const ROW_COLUMNS = "4.5rem 4.5rem minmax(0, 1fr) minmax(0, 12rem) max-content";

const OrderLinesPreview = ({ order }: { order: OrderToday }) => {
  const shown = order.lines.slice(0, MAX_LINE_THUMBNAILS);
  const extra = order.lines.length - shown.length;

  return (
    <Box display="flex" alignItems="center" gap={1} minWidth={0}>
      {shown.map(line => (
        <Box
          key={line.id}
          as="img"
          src={line.thumbnail?.url}
          alt=""
          width={10}
          height={10}
          flexShrink="0"
          borderRadius={2}
          borderColor="default1"
          borderWidth={1}
          borderStyle="solid"
          objectFit="scale-down"
        />
      ))}
      <Text size="small" color="default2" ellipsis>
        {order.lines[0]?.productName}
        {extra > 0 && (
          <>
            {" "}
            <FormattedMessage
              {...homeSalesPanelMessages.extraLines}
              values={{ count: extra }}
            />
          </>
        )}
      </Text>
    </Box>
  );
};

interface HomeSalesPanelProps {
  orders: OrderToday[];
  loading: boolean;
  ordersListUrl: string;
  awaitingPaymentCount: number;
  unpaidOrdersUrl: string;
}

export const HomeSalesPanel = ({
  orders,
  loading,
  ordersListUrl,
  awaitingPaymentCount,
  unpaidOrdersUrl,
}: HomeSalesPanelProps) => {
  const intl = useIntl();

  const awaitingPayment = awaitingPaymentCount > 0 && (
    <Box
      padding={3}
      borderColor="default1"
      borderWidth={1}
      borderTopStyle="solid"
    >
      <Link to={unpaidOrdersUrl}>
        <Text size="small" color="accent1">
          {intl.formatMessage(homeSalesPanelMessages.awaitingPayment, {
            count: awaitingPaymentCount,
          })}
        </Text>
      </Link>
    </Box>
  );

  if (loading) {
    return (
      <Box padding={3} display="flex" flexDirection="column" gap={2}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (orders.length === 0) {
    // A day can have unpaid orders and still no contributing ones, so the
    // awaiting line has to survive the empty state.
    return (
      <Box display="flex" flexDirection="column">
        <Box padding={3}>
          <Text size="small" color="default2">
            {intl.formatMessage(homeSalesPanelMessages.noOrders)}
          </Text>
        </Box>
        {awaitingPayment}
      </Box>
    );
  }

  // Every loaded order is listed: the dialog scrolls, and the headline and
  // the rows stay the same set by construction.
  return (
    <Box display="flex" flexDirection="column">
      <List>
        {orders.map(order => (
          <HomeProductListItem
            key={order.id}
            linkUrl={orderUrl(order.id)}
            dataTestId="sales-order-row"
          >
            <Box
              display="grid"
              __gridTemplateColumns={ROW_COLUMNS}
              alignItems="center"
              gap={4}
              width="100%"
            >
              <Text size="small">#{order.number}</Text>
              <Text size="small" color="default2">
                {intl.formatTime(order.created)}
              </Text>
              <OrderLinesPreview order={order} />
              <Text size="small" color="default2" ellipsis>
                {order.userEmail}
              </Text>
              <Text size="small" textAlign="right">
                <Money money={order.totalCharged} />
              </Text>
            </Box>
          </HomeProductListItem>
        ))}
      </List>
      <Box display="flex" justifyContent="flex-end" padding={3}>
        <Link to={ordersListUrl}>
          <Text size="small" color="accent1">
            {intl.formatMessage(homeSalesPanelMessages.viewAllOrders)}
          </Text>
        </Link>
      </Box>
      {awaitingPayment}
    </Box>
  );
};
